/**
 * The compte-rendu render/preview — the frozen RES-4 composition (analyse / valeur / unité /
 * intervalle / drapeau) plus the emission metadata (signature hash, emis_at). Read-only: the lines
 * are the snapshot the server froze at render; nothing is recomputed here (médico-légal). Numerics
 * use the mono/tabular face and stay LTR inside an RTL layout.
 */
import { Card, Table } from "@/ui";
import type { Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useToast } from "@/ui";
import type { CompteRendu, CompteRenduLigne } from "../types";
import { formatDateTime, shortHash } from "../format";
import { DrapeauBadge } from "./StatusBadges";

function intervalleText(l: CompteRenduLigne): string {
  const r = l.intervalle_reference;
  if (r == null) return "—";
  if (typeof r === "string") return r;
  if (typeof r === "object") {
    const o = r as Record<string, unknown>;
    const lo = o.borne_basse ?? o.basse ?? o.min ?? o.low;
    const hi = o.borne_haute ?? o.haute ?? o.max ?? o.high;
    if (lo != null || hi != null) return `${lo ?? ""} – ${hi ?? ""}`.trim();
    if (typeof o.texte === "string") return o.texte;
  }
  return "—";
}

export function CompteRenduPreview({ cr }: { cr: CompteRendu }) {
  const { t, locale } = useT();
  const online = useUiStore((s) => s.online);
  const toast = useToast();

  const columns: ReadonlyArray<Column<CompteRenduLigne>> = [
    {
      key: "analyse_code",
      header: t("results.preview.col.code"),
      mono: true,
      cell: (l) => <span className="nv-mono nv-ltr-island">{l.analyse_code}</span>,
      sortable: true,
      sortAccessor: (l) => l.analyse_code,
    },
    {
      key: "libelle",
      header: t("results.preview.col.libelle"),
      cell: (l) => l.libelle || "—",
    },
    {
      key: "valeur",
      header: t("results.preview.col.valeur"),
      mono: true,
      align: "end",
      cell: (l) => <span className="nv-mono nv-ltr-island">{l.valeur || "—"}</span>,
    },
    {
      key: "unite",
      header: t("results.preview.col.unite"),
      mono: true,
      cell: (l) => <span className="nv-mono nv-ltr-island">{l.unite ?? "—"}</span>,
    },
    {
      key: "intervalle",
      header: t("results.preview.col.intervalle"),
      mono: true,
      cell: (l) => <span className="nv-mono nv-ltr-island">{intervalleText(l)}</span>,
    },
    {
      key: "drapeau",
      header: t("results.preview.col.drapeau"),
      align: "center",
      cell: (l) => <DrapeauBadge drapeau={l.drapeau} />,
    },
  ];

  const lignes = [...cr.lignes].sort((a, b) => a.ordre - b.ordre);

  const copyHash = async () => {
    if (!cr.signature_hash) return;
    try {
      await navigator.clipboard.writeText(cr.signature_hash);
      toast.success(t("results.copied"));
    } catch {
      /* clipboard blocked — non-fatal */
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
      {cr.etat === "genere" && (
        <p
          role="status"
          style={{
            margin: 0,
            padding: "var(--nv-s-3)",
            borderRadius: "var(--nv-r-sm)",
            background: "var(--nv-surface-2)",
            color: "var(--nv-graphite)",
            fontSize: "var(--nv-fs-caption)",
          }}
        >
          ○ {t("results.preview.notEmitted")}
        </p>
      )}

      <Card title={t("results.preview.lines")} padded={false}>
        <Table<CompteRenduLigne>
          columns={columns}
          rows={lignes}
          rowKey={(l) => l.id}
          caption={t("results.preview.lines")}
          emptyTitle={t("results.preview.noLines")}
          {...(lignes.length > 12 ? { pageSize: 12 } : {})}
        />
      </Card>

      <p style={{ margin: 0, color: "var(--nv-steel)", fontSize: "var(--nv-fs-caption)" }}>
        {t("results.preview.frozen")}
      </p>

      {cr.etat === "emis" && (
        <Card title={t("results.preview.title")}>
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "var(--nv-s-2) var(--nv-s-5)",
              margin: 0,
              alignItems: "center",
            }}
          >
            <dt style={{ color: "var(--nv-slate)" }}>{t("results.meta.emisAt")}</dt>
            <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
              {formatDateTime(cr.emis_at, locale)}
            </dd>
            <dt style={{ color: "var(--nv-slate)" }}>{t("results.preview.signature")}</dt>
            <dd style={{ margin: 0, display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
              <span className="nv-mono nv-ltr-island" title={cr.signature_hash ?? undefined}>
                {shortHash(cr.signature_hash)}
              </span>
              {cr.signature_hash && (
                <button
                  type="button"
                  className="nv-iconbtn"
                  aria-label={t("results.action.copy")}
                  onClick={copyHash}
                  disabled={!online}
                >
                  ⧉
                </button>
              )}
            </dd>
          </dl>
        </Card>
      )}
    </div>
  );
}
